import React, { useState, useEffect } from "react";
import { LoadingSpinner } from "../../../../../common/Loading";
import { ConfirmationModal } from '../../../../../common/Modal';

// Tabs
import AssignmentDetailsTab from "./tabs/AssignmentDetailsTab";
import EmployeeInfoTab from "./tabs/EmployeeInfoTab";
import CompensationTab from "./tabs/CompensationTab";
import TransactionModalHeader from "./components/TransactionModalHeader";
import TransactionTabNavigation from "./components/TransactionTabNavigation";
import TransactionModalFooter from "./components/TransactionModalFooter";
import ScheduleTab from "./tabs/ScheduleTab";
import OtherDetailsTab from "./tabs/OtherTransactionDetails";
import { SALARYTYPES } from "../../../../../utils/enums";
import { useSalaryRateMatcher } from "../../../../hooks/useSalaryRateMatcher";
import { TRANSACTION_SALARY_FILTER_MAP } from "../../../filters/FilterConfig";

export const EmployeeTransactionModal = ({
  userAccess,
  isOpen, onClose,
  mode,
  selectedData,
  options,
  salaryData,
  dropdownsLoading,
  onAdd, onUpdate,
  canEditAssignment = false, canEditCompensation = false,
  showGeneralMessage = showGeneralMessage
}) => {
  const isViewOnly = mode === "view";

  const isAssignmentDetailsTabEditable = userAccess?.isEmployeeTransactionAssignmentManager && !isViewOnly;
  const isCompensationTabEditable = userAccess?.isEmployeeTransactionCompensationManager && !isViewOnly;
  const isScheduleTabEditable = userAccess?.isEmployeeTransactionScheduleManager && !isViewOnly;

  const visibilityAccess = {
    isAssignmentDetailsTabVisible: userAccess?.isEmployeeTransactionAssignmentManager,
    isCompensationTabVisible: userAccess?.isEmployeeTransactionCompensationManager,
    isScheduleTabVisible: userAccess?.isEmployeeTransactionScheduleManager,
  }

  const canSave = !isViewOnly && (canEditAssignment || canEditCompensation);

  const [activeTab, setActiveTab] = useState("assignment-details");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({ title: "", message: "", action: null });

  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({}); // ADDED: Error state

  const { matchedRules, loading: loadingRates } = useSalaryRateMatcher(
    form,
    form.salaryType,
    TRANSACTION_SALARY_FILTER_MAP
  );

  // Reset/Populate form
  useEffect(() => {
    if (isOpen) {
      setActiveTab("assignment-details");
      setErrors({}); // ADDED: Reset errors on open
      if (selectedData) {
        setForm({
          ...selectedData,
          effectiveFrom: selectedData.effectiveFrom?.split('T')[0] || "",
          contractFrom: selectedData.contractFrom?.split('T')[0] || "",
          contractTo: selectedData.contractTo?.split('T')[0] || "",
          employeeData: selectedData.assignedEmployeePersonalInformationId ? {
            fullName: selectedData.employeeName,
            employeeId: selectedData.employeeNumber,
            position: selectedData.employeePosition || "N/A",
            department: selectedData.employeeDepartment || "N/A"
          } : null,
          recipientData: selectedData.recipientEmployeePersonalInformationId ? { fullName: selectedData.recipientName } : null,
          assignedToData: selectedData.assignedToEmployeePersonalInformationId ? { fullName: selectedData.assignedToName, employeeId: selectedData.assignedToEmployeeNumber || "", position: selectedData.assignedToPosition || "N/A" } : null,
          destinationHeadData: selectedData.destinationHeadId ? { fullName: selectedData.destinationHeadName, employeeId: selectedData.destinationHeadEmployeeNumber, position: selectedData.destinationHeadPosition || "N/A" } : null,
          autoPopulated: {
            employeeType: selectedData.employeeInformationEmployeeTypeDescription || "",
            destination: selectedData.employeeInformationDestinationDescription || "",
            regularTitle: selectedData.employeePosition || "",
            regularVessel: selectedData.employeeInformationVesselName || "",
            vesselType: selectedData.employeeInformationVesselTypeName || "",
          }
        });
      } else {
        setForm({ status: "pending", isActive: true, entitledToOvertime: false, autoPopulated: {} });
      }
    }
  }, [isOpen, selectedData]);

  // Salary Logic
  useEffect(() => {
    if (!form.salaryType || !salaryData) return;
    const empId = form.assignedEmployeePersonalInformationId;

    let amount = null;
    if (form.salaryType === SALARYTYPES.DAILY) amount = salaryData.daily.find(s => s.employeePersonalInformationId === parseInt(empId))?.salary;
    else if (form.salaryType === SALARYTYPES.MONTHLY) amount = salaryData.monthly.find(s => s.employeePersonalInformationId === parseInt(empId))?.salary;
    else if (form.salaryType === SALARYTYPES.QOUTABASED) amount = salaryData.quotaBased.find(s => s.employeePersonalInformationId === parseInt(empId))?.salary;
    else if (form.salaryType === SALARYTYPES.POSITIONDESIGNATION) amount = null;

    setForm(prev => ({
      ...prev,
      rateAmount: selectedData?.rateAmount ? selectedData.rateAmount : (amount || null)
    }));
  }, [form.salaryType, form.assignedEmployeePersonalInformationId, salaryData]);

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // ADDED: Clear error when user types
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // ADDED: Validation Logic
  const validateForm = () => {
    const newErrors = {};
    const requiredMessage = "This field is required";

    // Tab 1 Fields
    if (isAssignmentDetailsTabEditable) {
      if (!form.effectiveFrom) newErrors.effectiveFrom = requiredMessage;
      if (!form.recipientEmployeePersonalInformationId) newErrors.recipientEmployeePersonalInformationId = requiredMessage;
      if (!form?.isOnLeave && !form.destinationVesselId) newErrors.destinationVesselId = requiredMessage;
      if (!form.remarks) newErrors.remarks = requiredMessage;
    }

    // Tab 2 Fields
    if (isAssignmentDetailsTabEditable) {
      if (!form.assignedEmployeePersonalInformationId) newErrors.assignedEmployeePersonalInformationId = requiredMessage;
      if (!form.assignedToEmployeePersonalInformationId) newErrors.assignedToEmployeePersonalInformationId = requiredMessage;
      if (!form?.isOnLeave && !form.medicalClearanceId) newErrors.medicalClearanceId = requiredMessage;
      if (!form.tempEmployeeTypeId) newErrors.tempEmployeeTypeId = requiredMessage;
      if (!form.classificationId) newErrors.classificationId = requiredMessage;
      if (!form.departmentId) newErrors.departmentId = requiredMessage;
      if (!form.tempPositionId) newErrors.tempPositionId = requiredMessage;
      if (!form.tempVesselId) newErrors.tempVesselId = requiredMessage;
      if (!form.employmentId) newErrors.employmentId = requiredMessage;
      if (!form.destinationHeadId) newErrors.destinationHeadId = requiredMessage;
      // Only required when employee is on leave
      if (form?.isOnLeave && !form.tempEmployeeStatusId) newErrors.tempEmployeeStatusId = requiredMessage;
    }

    // Tab 3 Fields
    if (isCompensationTabEditable) {
      if (!form.payTypeId) newErrors.payTypeId = requiredMessage;
      if (!form.contractFrom) newErrors.contractFrom = requiredMessage;
      if (!form.contractTo) newErrors.contractTo = requiredMessage;
      if (!form.salaryType) newErrors.salaryType = requiredMessage;
      if (!form.maintenanceSalaryCombinationV2Id && form.salaryType == SALARYTYPES.MAINTENANCESALARYCOMBINATION) {
        newErrors.maintenanceSalaryCombinationV2Id = requiredMessage;
      }
      // Deprecated, since position designation will not be utilize anymore.
      if (!form.rateAmount && form.salaryType == SALARYTYPES.POSITIONDESIGNATION) {
        newErrors.rateAmount = requiredMessage;
      }
    }

    // Tab 4
    if (isScheduleTabEditable) {
      if (!form.shiftScheduleId) newErrors.shiftScheduleId = requiredMessage;
      if (!form.restDay) newErrors.restDay = requiredMessage;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Auto-switch to the first tab that has an error
      if ([
        "effectiveFrom",
        "recipientEmployeePersonalInformationId",
        "destinationVesselId",
        "remarks"
      ].some(f => newErrors[f])) {
        setActiveTab("assignment-details");
      } else if ([
        "assignedEmployeePersonalInformationId",
        "assignedToEmployeePersonalInformationId",
        "medicalClearanceId",
        "tempEmployeeTypeId",
        "classificationId",
        "departmentId",
        "tempPositionId",
        "tempVesselId",
        "employmentId",
        "destinationHeadId"
      ].some(f => newErrors[f])) {
        setActiveTab("employee-info");
      } else if ([
        "payTypeId",
        "contractFrom",
        "contractTo",
        "restDay",
        "salaryType",
        "rateAmount"
      ].some(f => newErrors[f])) {
        setActiveTab("compensation");
      } else if (newErrors.shiftScheduleId) {
        setActiveTab("schedule");
      }
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    // MODIFIED: Run validation before showing confirmation
    if (!validateForm()) {
      showGeneralMessage("Validation failed. Please fill in all required fields across all tabs.", false);
      return;
    }

    const action = mode === "add"
      ? async () => { setConfirmLoading(true); await onAdd(form, () => { setShowConfirmation(false); onClose(); }); setConfirmLoading(false); }
      : async () => { setConfirmLoading(true); await onUpdate(form, selectedData.id, () => { setShowConfirmation(false); onClose(); }); setConfirmLoading(false); };

    setConfirmationConfig({
      title: mode === "add" ? "Create Transaction" : "Update Transaction",
      message: mode === "add" ? "Save this employee transaction?" : "Update this employee transaction?",
      action
    });
    setShowConfirmation(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto">
      <div className="bg-white w-full md:w-[90%] lg:w-[75%] rounded-lg shadow-2xl flex flex-col h-[90vh]">
        <TransactionModalHeader onClose={onClose} isViewOnly={isViewOnly} status={selectedData?.status} />
        <TransactionTabNavigation
          mode={mode}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          editableStatus={{
            assignment: isAssignmentDetailsTabEditable,
            compensation: isCompensationTabEditable,
            schedule: isScheduleTabEditable
          }}
          visibilityAccess={visibilityAccess}
        />

        <div className="p-6 overflow-y-auto flex-grow bg-white">
          {activeTab === "assignment-details" && <AssignmentDetailsTab form={form} set={setField} editable={isAssignmentDetailsTabEditable} errors={errors} />}
          {activeTab === "employee-info" && <EmployeeInfoTab form={form} set={setField} options={options} editable={isAssignmentDetailsTabEditable} errors={errors} />}
          {activeTab === "compensation" &&
            <CompensationTab
              form={form}
              set={setField}
              options={options}
              editable={isCompensationTabEditable}
              errors={errors}
              matchedRules={matchedRules}
              loadingRates={loadingRates}
            />
          }
          {activeTab === "schedule" && <ScheduleTab form={form} options={options} set={setField} editable={isScheduleTabEditable} errors={errors} />}
          {(activeTab === "other-info" && mode !== "add") && <OtherDetailsTab form={form} set={setField} editable={false} />}
        </div>

        <TransactionModalFooter onClose={onClose} canSave={canSave} handleSubmit={handleSubmit} mode={mode} />
      </div>

      <ConfirmationModal
        visible={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={confirmationConfig.action}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        isLoading={confirmLoading}
      />
      <LoadingSpinner loading={dropdownsLoading} />
    </div>
  );
};